import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import message from 'plugins/utils/message';
import anuncioRepository from 'repositories/anuncio_repository';

type FormModalAnunciosProps = {
  handleClose: () => void;
  openSettingsModal: boolean;
};

const ModalConfigAnuncio: React.FC<FormModalAnunciosProps> = ({
  handleClose,
  openSettingsModal,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const { t } = useTranslation();
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [currentAnuncioId, setCurrentAnuncioId] = useState<string | null>(null);

  const handleCloseSettings = () => {
    setPreviewImage(null);
    setSelectedFile(null);
    setCurrentAnuncioId(null);
    handleClose();
  };

  const getAnuncioActivo = async () => {
    setLoadingImage(true);
    try {
      const anuncio = await anuncioRepository.getActive();
      if (anuncio && anuncio.imageUrl) {
        setPreviewImage(anuncio.imageUrl);
        setCurrentAnuncioId(anuncio.id);
      } else {
        setPreviewImage(null);
        setCurrentAnuncioId(null);
      }
    } catch (error) {
      console.error('Error al obtener el anuncio activo:', error);
      setPreviewImage(null);
      setCurrentAnuncioId(null);
    } finally {
      setLoadingImage(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!currentAnuncioId) {
      message.error('No hay imagen para eliminar');
      return;
    }

    void message
      .confirm(t('anuncio.confirmarEliminar'), true)
      .then(async (confirmed) => {
        if (!confirmed) return;

        setDeleteLoading(true);
        try {
          await anuncioRepository.delete(currentAnuncioId);
          message.success(t('anuncio.anuncioEliminado'));
          setPreviewImage(null);
          setCurrentAnuncioId(null);
        } catch (error: unknown) {
          console.error('Error al eliminar la imagen:', error);
          const err = error as { response?: { data?: { message?: string } }; message?: string };
          const errorMessage =
            err.response?.data?.message ||
            err.message ||
            'Error al eliminar la imagen';
          message.error(errorMessage);
        } finally {
          setDeleteLoading(false);
        }
      });
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      if (!(file instanceof File)) {
        message.error('El objeto seleccionado no es un archivo válido');
        return;
      }

      if (file.size === 0) {
        message.error('El archivo está vacío');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        message.error('El archivo es demasiado grande (máximo 5MB)');
        return;
      }

      if (
        !file.type.startsWith('image/png') &&
        !file.type.startsWith('image/jpeg')
      ) {
        const messageText = t('errorFormatoImagen');
        message.error(messageText);
        return;
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveImage = async () => {
    if (!selectedFile) {
      message.error('No hay imagen seleccionada');
      return;
    }

    setLoading(true);

    try {
      await anuncioRepository.create({
        image: selectedFile,
      });
      message.success(t('anuncio.guardadoExito'));
      handleCloseSettings();
    } catch (error: unknown) {
      console.error('Error al guardar la imagen:', error);

      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Error al guardar la imagen';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!openSettingsModal) return;
    void getAnuncioActivo();
  }, [openSettingsModal]);

  const phonePreview = (content: React.ReactNode) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 1,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: { xs: 220, sm: 310 },
          height: { xs: 425, sm: 580 },
          backgroundColor: '#1a1a1a',
          borderRadius: '25px',
          padding: '8px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
          border: '2px solid #333',
        }}
      >
        {/* Notch */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 100,
            height: 25,
            backgroundColor: '#1a1a1a',
            borderRadius: '12px',
            zIndex: 2,
          }}
        />
        {/* Screen */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            backgroundColor: '#f5f5f5',
            borderRadius: '18px',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundColor: '#f5f5f5',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                height: 60,
                backgroundColor: '#fff',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pt: '20px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: '#333', fontWeight: 'bold', fontSize: '12px' }}
                >
                  Deutsche Schule La Paz
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              {(['80%', '60%', '70%'] as const).map((w, i) => (
                <Box
                  key={i}
                  sx={{
                    height: 20,
                    backgroundColor: '#e0e0e0',
                    borderRadius: 1,
                    width: w,
                  }}
                />
              ))}
            </Box>
            {content}
          </Box>
        </Box>
        {/* Home indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 5,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 80,
            height: 4,
            backgroundColor: '#666',
            borderRadius: '2px',
          }}
        />
      </Box>
    </Box>
  );

  const popupOverlay = (children: React.ReactNode) => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: 'auto',
          height: 'auto',
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '8px',
          mx: 1,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {children}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={openSettingsModal}
      onClose={handleClose}
      maxWidth="sm"
      fullScreen={isMobile}
      fullWidth
      slotProps={{ paper: { style: { borderRadius: 15 } } }}
    >
      <DialogTitle>
        <Grid container sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{t('anuncio.title')}</Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" color="textSecondary" align="center">
            {t('anuncio.titleMessage')}
          </Typography>

          {loadingImage ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography variant="body2" color="textSecondary">
                Loading...
              </Typography>
              {phonePreview(
                popupOverlay(
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2,
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="caption">Loading...</Typography>
                  </Box>,
                ),
              )}
            </Box>
          ) : previewImage ? (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              {phonePreview(
                popupOverlay(
                  <Box
                    component="img"
                    src={previewImage}
                    alt="Popup preview"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      borderRadius: 2,
                    }}
                  />,
                ),
              )}
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary" align="center">
                No hay imagen configurada actualmente
              </Typography>
            </Box>
          )}

          <input
            accept="image/*"
            type="file"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
            id="image-upload"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <label htmlFor="image-upload">
              <Fab
                color="primary"
                aria-label="upload"
                component="span"
                size="medium"
                title="Upload image"
              >
                <UploadIcon />
              </Fab>
            </label>

            {previewImage && !selectedFile && (
              <Fab
                color="secondary"
                aria-label="delete"
                component="span"
                size="medium"
                title="Delete image"
                onClick={handleDeleteImage}
                disabled={deleteLoading}
              >
                {deleteLoading ? <CircularProgress /> : <DeleteIcon />}
              </Fab>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleCloseSettings}
          sx={{
            maxWidth: '160px',
            backgroundColor: 'error.main',
            '&:hover': { backgroundColor: 'error.light' },
          }}
        >
          {t('form.cancelar')}
        </Button>
        <Button
          onClick={handleSaveImage}
          color="primary"
          variant="contained"
          disabled={!selectedFile || loading}
        >
          {loading ? 'Guardando...' : t('form.guardar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalConfigAnuncio;
